"use client";

import React, { useMemo } from "react";
import Link from "next/link";
import { Skeleton } from "./skeleton";
import { usePathname } from "next/navigation";
import {
  Package,
  Tag,
  LucideIcon,
  Menu,
  X,
  ArrowLeftToLine,
  ArrowRightToLine,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import { CustomTooltip } from "./tooltip";

interface NavRoute {
  name: string;
  path: string;
  Icon: LucideIcon;
  label?: string;
}

interface UserMenuProps {
  isOpen: boolean;
  onClose: () => void;
  setOpen?: (opened: boolean) => void;
  fullScreen?: boolean;
  defaultRoutes?: NavRoute[];
  /** true mientras se resuelve la sesión y aún no se conocen las rutas del rol. */
  isLoading?: boolean;
}

export const USER_ROUTES: NavRoute[] = [
  {
    name: "Categorías",
    path: "/admin/categorias",
    Icon: Tag,
    label: "Categorías",
  },
  {
    name: "Productos",
    path: "/admin/productos",
    Icon: Package,
    label: "Productos",
  },
];

export function UserMenu({
  isOpen,
  onClose,
  defaultRoutes = USER_ROUTES,
  isLoading = false,
  setOpen,
}: UserMenuProps) {
  const pathname = usePathname();

  const routes = useMemo(() => defaultRoutes, [defaultRoutes]);
  const loadingRoutes = isLoading || !routes || routes.length === 0;

  const toggleMenu = () => setOpen?.(!isOpen);

  function renderNavLink(route: NavRoute) {
    const isActive = pathname === route.path;

    const linkClass = cn(
      "group w-full flex items-center cursor-pointer font-semibold rounded-r-lg p-2 text-foreground transition-colors",
      isOpen ? "gap-2 text-[15px] justify-start" : "justify-center pl-3",
      isActive ? "bg-primary !text-white" : "hover:bg-primary hover:text-white",
    );

    const iconClass = cn(
      "shrink-0",
      isActive ? "!text-white" : "text-foreground group-hover:!text-white",
    );

    const link = (
      <Link
        href={route.path}
        className={linkClass}
        onClick={() => {
          if (window.innerWidth < 768) onClose();
        }}
      >
        <route.Icon size={20} className={iconClass} />
        {isOpen && <span className="truncate">{route.name}</span>}
      </Link>
    );

    if (!isOpen) {
      return (
        <CustomTooltip key={route.path} content={route.name} side="right">
          {link}
        </CustomTooltip>
      );
    }

    return <React.Fragment key={route.path}>{link}</React.Fragment>;
  }

  return (
    <div className="h-full overflow-hidden">
      {/* Mobile */}
      <div className="block md:hidden">
        {isOpen ? (
          <div className="transition-all duration-300 ease-in-out z-50 fixed inset-0 bg-white gap-4 flex items-start justify-start pt-16 flex-col pr-3">
            <div className="w-full overflow-y-auto max-h-[80vh]">
              <div
                className="flex justify-end w-full cursor-pointer p-4"
                onClick={() => setOpen?.(false)}
              >
                <X />
              </div>
              {loadingRoutes
                ? Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full mb-2" />
                  ))
                : routes.map((route) => renderNavLink(route))}
            </div>
          </div>
        ) : (
          <div className="flex justify-start pl-4 w-full p-2">
            <Button variant="ghost" onClick={() => setOpen?.(true)} size="lg">
              <Menu className="text-foreground hover:text-primary" />
            </Button>
          </div>
        )}
      </div>

      {/* Desktop */}
      <aside
        className={cn(
          "hidden md:flex flex-col h-full w-full transition-all duration-300 ease-in-out",
        )}
      >
        <div
          className={cn(
            "flex items-center shrink-0 p-2 border-b border-primary/10 mb-2",
            isOpen ? "justify-end" : "justify-center",
          )}
        >
          <CustomTooltip
            content={isOpen ? "Contraer menú" : "Expandir menú"}
            side="right"
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleMenu}
              className={cn(!isOpen && "w-full")}
            >
              {isOpen ? (
                <ArrowLeftToLine className="text-foreground" />
              ) : (
                <ArrowRightToLine className="text-foreground" />
              )}
            </Button>
          </CustomTooltip>
        </div>

        <nav className="flex flex-col gap-1 flex-1 overflow-y-auto scrollbar-thin pr-2">
          {loadingRoutes ? (
            Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full mb-2" />
            ))
          ) : (
            <>
              {routes.map((route) => renderNavLink(route))}
            </>
          )}
        </nav>
      </aside>
    </div>
  );
}
